//@ts-ignore
import { SUBJECTS_SLUGS } from '@yandex-int/k-common/constants';
import { LessonProblem } from '@yandex-int/k-common/typings';
import { Copy, Document, TrashClearKit, Edit as PencilIcon } from 'education-icons';
import { Button, Link, SimpleText, useStaticUrls } from 'platform-components';
import { POPUP_DIRECTIONS } from 'platform-components/constants';
import {
    withRouterPath,
    InjectedProps as WithRouterPathProps,
} from 'platform-components/src/utils/hocs/with-router-path/with-router-path';
import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';

import AdaptiveDots from 'common.components/adaptive-dots/adaptive-dots';
import { LIBRARY_CONTENT_TYPE } from 'common.components/constants';
import { PROBLEM_SIDE_PANEL_MODE } from 'common.components/lesson-view.common/constants';
import { LessonViewDetailedViewContent } from 'common.components/lesson-view.detailed-view/__content/detailed-view__content';
import { LessonViewDetailedViewHeader } from 'common.components/lesson-view.detailed-view/__header/detailed-view__header';
import { LessonViewDetailedViewReturnButton } from 'common.components/lesson-view.detailed-view/__return-button/detailed-view__return-button';
import { LessonViewDetailedView } from 'common.components/lesson-view.detailed-view/detailed-view';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { getCourseByClassAndId } from 'store/courses/selectors';
import { actions as problemsViewActions } from 'store/problems-view/actions';
import * as problemsViewSelectors from 'store/problems-view/selectors';
import * as problemActions from 'store/problems/actions';

import Paginator from './__paginator/problem-view__paginator';
import { ProblemViewProblemRunContainer } from './__problem-run/problem-view__problem-run';
import './problem-view.scss';
import InfoSidePanel from './__side-panel/problem-view__info-side-panel';
import VariantsSidePanel from './__side-panel/problem-view__variants-side-panel';
import * as selectors from './selectors';

interface HOCProps extends WithRouterPathProps {}

interface OwnProps {
    returnButtonTextId?: string;
}

interface StoreProps {
    isHeadless: boolean;
    isPreparedLesson: boolean;
    selectedProblem: LessonProblem | null;
    selectedProblemId: number | null;
    shouldShowProblemInfoControl: boolean;
    shouldShowProblemVariantsControl: boolean;
    stackId: number | null;
    shouldShowProblemEditButton: boolean;
    cLessonId: number | null;
    courseId: number | null;
    shouldShowDeleteProblemButton: boolean;
    sidePanelMode: PROBLEM_SIDE_PANEL_MODE;
    subject: SUBJECTS_SLUGS | undefined;
}

interface DispatchProps {
    getProblemsRequest: typeof problemActions.getProblemsRequest;
    removeProblemFromProblemView: typeof lessonViewActions.removeProblemFromProblemViewAsync;
    setSelectedProblemId: typeof problemsViewActions.setSelectedProblemId;
    setProblemSidePanelMode: typeof lessonViewActions.setProblemSidePanelMode;
}

interface Props extends OwnProps, StoreProps, DispatchProps, HOCProps {}

export const ProblemView = (props: Props) => {
    const {
        getProblemsRequest,
        isHeadless,
        isPreparedLesson,
        selectedProblemId,
        setSelectedProblemId,
        shouldShowProblemInfoControl,
        shouldShowProblemEditButton,
        shouldShowProblemVariantsControl,
        getRouterPath,
        cLessonId,
        courseId,
        stackId,
        subject,
        shouldShowDeleteProblemButton,
        returnButtonTextId,
        setProblemSidePanelMode,
        sidePanelMode,
    } = props;
    const staticUrls = useStaticUrls();

    const getEditProblemPath = () =>
        getRouterPath(staticUrls.cmCourseLessonProblemEdit, {
            courseId,
            clessonId: cLessonId,
            problemId: selectedProblemId,
        });

    const headerControlsRef = useRef<HTMLDivElement>(null);

    const paginatorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (stackId) {
            getProblemsRequest(stackId);
        }
    }, [getProblemsRequest, stackId]);

    const shouldShowProblemViewSidePanel = sidePanelMode !== PROBLEM_SIDE_PANEL_MODE.CLOSED;

    const handleClickChangeVariants = () => {
        handleToggleSidePanel(PROBLEM_SIDE_PANEL_MODE.VARIANTS);
    };

    const handleToggleSidePanel = (mode: PROBLEM_SIDE_PANEL_MODE) => {
        if (sidePanelMode === mode) {
            setProblemSidePanelMode({ mode: PROBLEM_SIDE_PANEL_MODE.CLOSED });

            return;
        }

        setProblemSidePanelMode({ mode: mode });
    };

    const handleRemoveProblem = () => {
        const { removeProblemFromProblemView } = props;

        if (!selectedProblemId) {
            return;
        }

        ym('reachGoal', 'DeleteOneContent', {
            subject,
            stackId,
            problemId: selectedProblemId,
        });

        removeProblemFromProblemView({ problemId: selectedProblemId });
    };

    const renderSidePanel = (): React.ReactElement | null => {
        switch (sidePanelMode) {
            case PROBLEM_SIDE_PANEL_MODE.INFO:
                return (
                    <InfoSidePanel
                        ignoredElements={[headerControlsRef, paginatorRef]}
                        onOutsideClick={() => setProblemSidePanelMode({ mode: PROBLEM_SIDE_PANEL_MODE.CLOSED })}
                    />
                );
            case PROBLEM_SIDE_PANEL_MODE.VARIANTS:
                return (
                    <VariantsSidePanel
                        ignoredElements={[headerControlsRef]}
                        onOutsideClick={() => setProblemSidePanelMode({ mode: PROBLEM_SIDE_PANEL_MODE.CLOSED })}
                    />
                );
            default:
                return null;
        }
    };

    const handleClickProblemInfo = () => {
        handleToggleSidePanel(PROBLEM_SIDE_PANEL_MODE.INFO);

        ym('reachGoal', 'teacher_lesson_click_recos', {
            subject,
            themeId: stackId,
            problemId: selectedProblemId,
        });
    };

    const handlePaginatorProblemClick = (problemId: number | null) => {
        ym('reachGoal', 'teacher_navigation_card_carousel', {
            clessonId: cLessonId,
            contentType: isPreparedLesson ? LIBRARY_CONTENT_TYPE.PREPARED_LESSON : LIBRARY_CONTENT_TYPE.COURSE_LESSON,
            subject,
            problemId: selectedProblemId,
            type: 'carousel',
        });

        setSelectedProblemId(problemId || null);
    };

    const handleReturnButtonClick = () => {
        ym('reachGoal', 'teacher_lesson_creation_back_to_lesson_cards', {
            clessonId: cLessonId,
            contentType: isPreparedLesson ? LIBRARY_CONTENT_TYPE.PREPARED_LESSON : LIBRARY_CONTENT_TYPE.COURSE_LESSON,
            subject,
        });

        setSelectedProblemId(null);
    };

    const getAccentByMode = (mode: PROBLEM_SIDE_PANEL_MODE) => {
        switch (mode) {
            case PROBLEM_SIDE_PANEL_MODE.VARIANTS:
            case PROBLEM_SIDE_PANEL_MODE.INFO:
                return Button.ACCENT.NORMAL;
            default:
                return undefined;
        }
    };

    const controls = [
        shouldShowProblemVariantsControl && (
            <Button
                accent={getAccentByMode(sidePanelMode)}
                className="qa_problem-view-change-variant"
                data-key="variants"
                icon={<Copy />}
                onClick={handleClickChangeVariants}
                view={Button.VIEW.CLEAR}
            >
                <SimpleText id="problemView.variants" />
            </Button>
        ),
        shouldShowProblemInfoControl && (
            <Button
                accent={getAccentByMode(sidePanelMode)}
                data-key="info"
                icon={<Document />}
                onClick={handleClickProblemInfo}
                view={Button.VIEW.CLEAR}
            >
                <SimpleText id="problemView.recommendations" />
            </Button>
        ),
        shouldShowProblemEditButton && (
            <Link data-key="edit-problem" to={getEditProblemPath()}>
                <Button data-key="edit-problem" icon={<PencilIcon />} view={Button.VIEW.CLEAR}>
                    <SimpleText id="library.editProblem" />
                </Button>
            </Link>
        ),
        shouldShowDeleteProblemButton && (
            <Button
                accent={Button.ACCENT.DANGER}
                className="qa_problem-view-delete-problem"
                data-key="delete-problem"
                icon={<TrashClearKit />}
                onClick={handleRemoveProblem}
                view={Button.VIEW.CLEAR}
            >
                <SimpleText id="problemView.removeProblem" />
            </Button>
        ),
    ].filter(Boolean) as Array<JSX.Element>;

    return (
        <LessonViewDetailedView mix="problem-view">
            <LessonViewDetailedViewHeader isHeadless={isHeadless} mix="problem-view__header" ref={headerControlsRef}>
                <>
                    <LessonViewDetailedViewReturnButton
                        mix="problem-view__return-button"
                        onClick={handleReturnButtonClick}
                        textId={returnButtonTextId || 'problemView.goToProblemList'}
                    />
                    <AdaptiveDots
                        direction={POPUP_DIRECTIONS.LEFT}
                        mix="problem-view__header-controls"
                        popupProps={{
                            mix: 'problem-view__header-controls-popup',
                            inLayer: false,
                            selfclosable: true,
                        }}
                    >
                        {controls.map((item) => (
                            <AdaptiveDots.Item
                                key={item.props['data-key']}
                                mix="problem-view__header-menu-item-wrapper"
                                type={AdaptiveDots.TYPES.PLAIN}
                            >
                                {item}
                            </AdaptiveDots.Item>
                        ))}
                    </AdaptiveDots>
                </>
            </LessonViewDetailedViewHeader>
            {shouldShowProblemViewSidePanel && renderSidePanel()}
            <LessonViewDetailedViewContent mix="problem-view__content">
                <ProblemViewProblemRunContainer />
                <Paginator innerRef={paginatorRef} onProblemItemClick={handlePaginatorProblemClick} />
            </LessonViewDetailedViewContent>
        </LessonViewDetailedView>
    );
};

const mapStateToProps = (storeState: any, ownProps: HOCProps): StoreProps => {
    const {
        match: {
            params: { classId, subject },
        },
    } = ownProps;

    const stackId = lessonViewSelectors.getProblemStackId(storeState);
    const courseId = lessonSelectors.getLessonCourseId(storeState);
    const course = getCourseByClassAndId(storeState, { classId, courseId });

    return {
        isHeadless: lessonViewSelectors.getIsHeadless(storeState),
        isPreparedLesson: lessonSelectors.getIsPreparedLesson(storeState),
        selectedProblem: lessonViewSelectors.getSelectedProblemLink(storeState),
        selectedProblemId: problemsViewSelectors.getSelectedProblemId(storeState),
        shouldShowProblemInfoControl: selectors.getShouldShowProblemInfoControl(storeState),
        shouldShowProblemEditButton: selectors.getShouldShowProblemEditButton(storeState),
        cLessonId: lessonSelectors.getCLessonId(storeState),
        courseId,
        shouldShowProblemVariantsControl: selectors.getShouldShowProblemVariantsControl(storeState, {
            stackId,
            subject,
        }),
        stackId,
        shouldShowDeleteProblemButton: selectors.getShouldShowDeleteProblemButton(storeState),
        sidePanelMode: lessonViewSelectors.getProblemSidePanelMode(storeState),
        subject: course?.subject,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            getProblemsRequest: problemActions.getProblemsRequest,
            removeProblemFromProblemView: lessonViewActions.removeProblemFromProblemViewAsync,
            setSelectedProblemId: problemsViewActions.setSelectedProblemId,
            setProblemSidePanelMode: lessonViewActions.setProblemSidePanelMode,
        },
        dispatch
    );
};

export default withRouterPath(connect(mapStateToProps, mapDispatchToProps)(ProblemView));
